import React from "react";
import { X, Plus, Minus, ShoppingBag, Trash2, ArrowRight, Flame } from "lucide-react";
import { useCart } from "@/lib/cartContext";
import { formatHUF } from "@/lib/data";

export default function CartDrawer() {
  const { items, total, count, open, setOpen, removeItem, updateQty, clear } = useCart();

  const handleCheckout = () => {
    // Placeholder — Stripe integration not yet wired here
    alert("Hamarosan elérhető — átirányítás a Stripe biztonságos fizetéshez.");
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[70] bg-pkcharcoal/70 backdrop-blur-sm transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        data-testid="cart-drawer"
        className={`fixed top-0 right-0 z-[80] h-full w-full sm:w-[440px] bg-white shadow-2xl flex flex-col transition-transform duration-500 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Kosár"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-6 py-5 border-b border-pkash bg-white">
          <div className="flex items-center gap-3">
            <div className="relative">
              <ShoppingBag className="w-5 h-5 text-pkred" strokeWidth={1.5} />
              {count > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-pkred text-white text-[0.55rem] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </div>
            <div>
              <div className="text-[0.6rem] tracking-[0.3em] uppercase text-pkred font-semibold">
                Kosarad
              </div>
              <div className="font-serif text-xl text-pkcharcoal leading-none mt-0.5">
                {count > 0 ? `${count} darab` : "Üres"}
              </div>
            </div>
          </div>
          <button
            data-testid="cart-close"
            onClick={() => setOpen(false)}
            aria-label="Kosár bezárása"
            className="w-9 h-9 rounded-full border border-pkash flex items-center justify-center hover:border-pkred hover:text-pkred transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.6} />
          </button>
        </header>

        {/* Items */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-8 py-20">
              <div className="w-16 h-16 rounded-full bg-pkbeige flex items-center justify-center">
                <Flame className="w-6 h-6 text-pkred" strokeWidth={1.4} />
              </div>
              <div className="font-serif text-2xl text-pkcharcoal mt-6 leading-tight">
                A tűz <span className="italic text-pkred">vár.</span>
              </div>
              <p className="text-pkcharcoal/60 text-sm mt-3 max-w-xs">
                Még nincs deszka a kosaradban. Nézd meg a limitált kollekciót és tedd be a kedvenced.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-pkash">
              {items.map((p) => (
                <li
                  key={p.id}
                  data-testid={`cart-item-${p.id}`}
                  className="flex gap-4 px-6 py-5"
                >
                  <div className="w-20 h-24 shrink-0 overflow-hidden bg-pkbeige">
                    <img
                      src={p.img}
                      alt={p.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.25em] uppercase text-pkcharcoal/55 font-semibold">
                      {p.sub}
                    </div>
                    <div className="font-serif text-lg text-pkcharcoal leading-tight mt-0.5 truncate">
                      {p.title}
                    </div>
                    <div className="text-pkred font-semibold text-sm mt-1.5">
                      {formatHUF(p.price)}
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="inline-flex items-center border border-pkash">
                        <button
                          data-testid={`qty-minus-${p.id}`}
                          onClick={() => updateQty(p.id, p.qty - 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-pkbeige transition-colors"
                          aria-label="Csökkent"
                        >
                          <Minus className="w-3 h-3" strokeWidth={2} />
                        </button>
                        <span className="w-8 text-center text-sm font-semibold">
                          {p.qty}
                        </span>
                        <button
                          data-testid={`qty-plus-${p.id}`}
                          onClick={() => updateQty(p.id, p.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-pkbeige transition-colors"
                          aria-label="Növel"
                        >
                          <Plus className="w-3 h-3" strokeWidth={2} />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${p.id}`}
                        onClick={() => removeItem(p.id)}
                        className="text-pkcharcoal/45 hover:text-pkred transition-colors"
                        aria-label="Eltávolít"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.6} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <footer className="border-t border-pkash bg-white p-6">
            <div className="flex items-end justify-between mb-5">
              <div>
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-pkcharcoal/55 font-semibold">
                  Végösszeg
                </div>
                <div className="text-xs text-pkcharcoal/50 mt-1">
                  Szállítás külön egyeztetés szerint
                </div>
              </div>
              <div className="font-serif text-3xl text-pkred font-medium leading-none">
                {formatHUF(total)}
              </div>
            </div>

            <button
              data-testid="cart-checkout"
              onClick={handleCheckout}
              className="btn-fire w-full"
            >
              Tovább a fizetéshez
              <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
            </button>
            <button
              data-testid="cart-clear"
              onClick={clear}
              className="block mx-auto mt-3 text-[0.65rem] tracking-[0.2em] uppercase text-pkcharcoal/50 hover:text-pkred font-semibold transition-colors"
            >
              Kosár ürítése
            </button>
            <p className="mt-4 text-[0.65rem] text-center text-pkcharcoal/40 leading-relaxed">
              A fizetés Stripe-on keresztül, biztonságosan történik. Hamarosan elérhető.
            </p>
          </footer>
        )}
      </aside>
    </>
  );
}
